import { FC } from "react";

import { type SectionEnrollmentData } from "api/api-data-types";
import { ScrollableTableContainer } from "components/tables/ScrollableTableContainer";
import { TD, TH, THead, TR, Table } from "components/tables/TableElements";
import { t } from "i18n/i18n";
import { Tooltip } from "mds/components/Tooltip";

type CourseEnrollmentTableProps = {
  data: SectionEnrollmentData[];
};

export const CourseEnrollmentTable: FC<CourseEnrollmentTableProps> = ({ data }) => {
  return (
    <ScrollableTableContainer className="mb-0 max-h-[330px] overflow-y-auto">
      <Table>
        <THead>
          <tr>
            <TH>
              <Tooltip place="top" text={t("course_enrollment_report.columns.term_tooltip")}>
                {t("course_enrollment_report.columns.term")}
              </Tooltip>
            </TH>

            <TH>
              <Tooltip place="top" text={t("course_enrollment_report.columns.course_code_tooltip")}>
                {t("course_enrollment_report.columns.course_code")}
              </Tooltip>
            </TH>

            <TH>
              <Tooltip
                place="top"
                text={t("course_enrollment_report.columns.course_title_tooltip")}
              >
                {t("course_enrollment_report.columns.course_title")}
              </Tooltip>
            </TH>

            <TH>
              <Tooltip place="top" text={t("course_enrollment_report.columns.role_tooltip")}>
                {t("course_enrollment_report.columns.role")}
              </Tooltip>
            </TH>

            <TH>
              <Tooltip place="top" text={t("course_enrollment_report.columns.name_tooltip")}>
                {t("course_enrollment_report.columns.name")}
              </Tooltip>
            </TH>

            <TH>
              <Tooltip place="top" text={t("course_enrollment_report.columns.email_tooltip")}>
                {t("course_enrollment_report.columns.email")}
              </Tooltip>
            </TH>

            <TH>
              <Tooltip
                place="top"
                text={t("course_enrollment_report.columns.disabled_status_tooltip")}
              >
                {t("course_enrollment_report.columns.disabled_status")}
              </Tooltip>
            </TH>

            <TH>
              <Tooltip
                place="top"
                text={t("course_enrollment_report.columns.external_user_id_tooltip")}
              >
                {t("course_enrollment_report.columns.external_user_id")}
              </Tooltip>
            </TH>

            <TH>
              <Tooltip
                place="top"
                text={t("course_enrollment_report.columns.external_course_id_tooltip")}
              >
                {t("course_enrollment_report.columns.external_course_id")}
              </Tooltip>
            </TH>

            <TH>
              <Tooltip
                place="top"
                text={t("course_enrollment_report.columns.invite_status_tooltip")}
              >
                {t("course_enrollment_report.columns.invite_status")}
              </Tooltip>
            </TH>
          </tr>
        </THead>

        <tbody>
          {data.map((row) => (
            <TR key={`${row.email}-${row.course_title}`}>
              <TD className="truncate">
                <Tooltip place="left-start" text={row.term} wrapperClassName="truncate">
                  {row.term}
                </Tooltip>
              </TD>

              <TD className="truncate">
                <Tooltip place="left-start" text={row.course_code} wrapperClassName="truncate">
                  {row.course_code}
                </Tooltip>
              </TD>

              <TD>
                <Tooltip place="left-start" text={row.course_title}>
                  {row.course_title}
                </Tooltip>
              </TD>

              <TD className="truncate">
                <Tooltip
                  place="left-start"
                  text={t(`course_enrollment_report.roles.${row.role}`)}
                  wrapperClassName="truncate"
                >
                  <span className="inline-flex items-center rounded-full py-1 text-xs font-medium">
                    {t(`course_enrollment_report.roles.${row.role}`)}
                  </span>
                </Tooltip>
              </TD>

              <TD className="truncate">
                <Tooltip place="left-start" text={row.name} wrapperClassName="truncate">
                  {row.name}
                </Tooltip>
              </TD>

              <TD className="truncate">
                <Tooltip place="left-start" text={row.email} wrapperClassName="truncate">
                  {row.email}
                </Tooltip>
              </TD>

              <TD className="truncate">
                <Tooltip
                  place="left-start"
                  text={t(`course_enrollment_report.disabled_status_values.${row.disabled_status}`)}
                  wrapperClassName="truncate"
                >
                  {t(`course_enrollment_report.disabled_status_values.${row.disabled_status}`)}
                </Tooltip>
              </TD>

              <TD className="truncate">
                <Tooltip
                  place="left-start"
                  text={row.external_user_id || "-"}
                  wrapperClassName="truncate"
                >
                  {row.external_user_id || "-"}
                </Tooltip>
              </TD>

              <TD className="truncate">
                <Tooltip
                  place="left-start"
                  text={row.external_course_id || "-"}
                  wrapperClassName="truncate"
                >
                  {row.external_course_id || "-"}
                </Tooltip>
              </TD>

              <TD className="truncate">
                <Tooltip
                  place="left-start"
                  text={t(`course_enrollment_report.invite_status.${row.invite_status}`)}
                  wrapperClassName="truncate"
                >
                  {t(`course_enrollment_report.invite_status.${row.invite_status}`)}
                </Tooltip>
              </TD>
            </TR>
          ))}
        </tbody>
      </Table>
    </ScrollableTableContainer>
  );
};
